import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import { Shield, ExternalLink, ArrowRight } from "lucide-react";

export default function PrivacyPoliciesPage() {
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    const fetchApps = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(`${API_URL}/api/apps`);
        // Filter apps that have privacy policies
        const appsWithPrivacy = response.data.filter(app => app.privacyPolicyUrl || app.privacyPolicyText);
        setApps(appsWithPrivacy);
      } catch (err) {
        console.error("Error fetching apps:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchApps();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="mx-auto max-w-7xl px-6 pt-16 pb-12">
        <div className="text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <Shield className="w-12 h-12 text-cyan-400" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            Privacy Policies
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-slate-400">
            Transparency and trust are at the core of everything we do. Review the privacy policies for each of our applications.
          </p>
        </div>
      </section>

      {/* Privacy Policies Grid */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        {loading ? (
          <div className="text-center text-slate-400 py-12">
            Loading privacy policies...
          </div>
        ) : apps.length === 0 ? (
          <div className="text-center text-slate-400 py-12">
            No privacy policies available yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app) => (
              <Link
                key={app.slug}
                to={`/privacy/${app.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/50 p-6 transition-all hover:border-cyan-400/30 hover:bg-slate-900/70"
              >
                {/* App Logo */}
                {app.logo && (
                  <div className="mb-4">
                    <img
                      src={app.logo}
                      alt={`${app.name} logo`}
                      className="w-16 h-16 object-contain rounded-lg"
                    />
                  </div>
                )}

                {/* App Name */}
                <h3 className="text-xl font-bold mb-2 group-hover:text-cyan-400 transition-colors">
                  {app.name}
                </h3>

                {/* Category */}
                <p className="text-sm text-slate-500 mb-4">
                  {app.category}
                </p>

                {/* Description Preview */}
                <p className="text-sm text-slate-400 line-clamp-2 mb-4">
                  {app.description}
                </p>

                {/* View Policy Button */}
                <div className="flex items-center gap-2 text-sm text-cyan-400 group-hover:gap-3 transition-all">
                  <span className="font-medium">View Privacy Policy</span>
                  <ArrowRight className="w-4 h-4" />
                </div>

                {/* Hover Effect Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/0 via-cyan-400/0 to-cyan-400/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl pointer-events-none" />
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Footer Note */}
      <section className="mx-auto max-w-7xl px-6 py-12 border-t border-white/10">
        <div className="text-center text-sm text-slate-500">
          <p className="mb-2">
            If you have any questions about our privacy policies, please contact us at{" "}
            <a
              href="mailto:Support@perfectpeaceenterprise.com"
              className="text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              Support@perfectpeaceenterprise.com
            </a>
          </p>
          <p>
            <Link to="/" className="text-cyan-400 hover:text-cyan-300 transition-colors">
              Return to Homepage
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
